import { useCallback, useId, useState, type ReactNode } from 'react';
import {
  CaretDownIcon,
  CaretRightIcon,
  DotsSixVerticalIcon,
  PlusIcon,
  UploadSimpleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import { Textarea } from 'src/components/ui/textarea';
import type { ExtensionTargetModel } from '@erp/shared-types';
import { usePreviewAssistImportFields } from 'src/api/rpc/hooks/core/useSchemaDefinitions';
import { useTargetModelLabel } from 'src/components/common/schema-extensions/extensionTargetLabels';
import { rpcClient } from 'src/api/rpc/client';
import type { ImportFieldNode } from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';
import { JsonFieldImportDialog } from './JsonFieldImportDialog';
import { cn } from 'src/lib/utils';
import { ExtensionEntitySelect } from './ExtensionEntitySelect';
import { QuickSchemaFieldRow } from './QuickSchemaFieldRow';
import { useQuickSchemaIssueToasts } from './useQuickSchemaIssueToasts';
import {
  planQuickSchemaSave,
  saveQuickSchema,
  type QuickSchemaSaveApi,
} from './quickSchemaSave';
import { SqlSchemaFieldRow } from './SqlSchemaFieldRow';
import {
  addChildToRow,
  defaultChildRowForParent,
  findRowInTree,
  importNodesToRows,
  newRow,
  normalizeRootFieldKey,
  removeRowFromTree,
  updateRowInTree,
  FRIENDLY_FIELD_TYPES,
  SQL_QUICK_ADD_FIELD_TYPES,
  type FriendlyFieldRow,
} from './quickSchemaFieldTree';

const QUERY_KEY_BASE = 'schema-definitions';

const quickSchemaSaveApi: QuickSchemaSaveApi = {
  create: (input) => rpcClient.core.schemaDefinitions.create(input),
  importFields: (input) => rpcClient.core.schemaDefinitions.importFields(input),
  update: (input) => rpcClient.core.schemaDefinitions.update(input),
  publishVersion: (input) =>
    rpcClient.core.schemaDefinitions.publishVersion(input),
  register: (input) => rpcClient.core.schemaExtensions.register(input),
  delete: (input) => rpcClient.core.schemaDefinitions.delete(input),
};

/** Static, so it is built once rather than on every render of the form. */
const SUBTITLE_NODE = (
  <p className="text-left text-xs font-normal text-muted-foreground">
    <T context="subtitle quick schema dialog">
      Define fields and nested array/object shapes. Nothing is saved until you
      click Save.
    </T>
  </p>
);

/**
 * What the dialog creates. `extension` is an entity extension: a `sql` schema
 * registered against an entity on save, so its fields become extra columns on
 * that entity. It is not a stored schema type.
 */
export type QuickSchemaType = 'extraction' | 'extension' | 'sql';

const DEFAULT_ALLOWED_SCHEMA_TYPES: readonly QuickSchemaType[] = ['extraction'];

export type QuickSchemaDefaults = {
  schemaName?: string;
  targetModel?: string;
  schemaType?: QuickSchemaType;
};

/**
 * How the form is presented. `sheet` slides in from the right and is the option
 * that works from inside another dialog, where a second centered modal reads as
 * a stack.
 */
export type QuickSchemaPresentation = 'dialog' | 'sheet';

export type QuickSchemaFieldsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Defaults to `dialog`. */
  presentation?: QuickSchemaPresentation;
  /**
   * Fields to prefill the grid with, e.g. inferred from a sample of real data.
   * Read once when the form mounts; the user edits from there.
   */
  initialFields?: ImportFieldNode[];
  /** Optional; default is a generic "New schema" title. */
  title?: ReactNode;
  defaults?: QuickSchemaDefaults;
  /**
   * Which schema types this call site's "Schema type" select offers.
   * Defaults to extraction only — pass `['extraction', 'extension', 'sql']`
   * for call sites where creating an entity extension or a standalone "sql"
   * (1:1 Postgres-column) schema makes sense, e.g. the general schema
   * definitions settings page.
   */
  allowedSchemaTypes?: readonly QuickSchemaType[];
  onComplete: (result: {
    schemaDefinitionId: string;
    publishedVersionId: string;
  }) => void | Promise<void>;
};

function schemaTypeSelectLabel(
  gt: ReturnType<typeof useGT>,
  type: QuickSchemaType,
): string {
  if (type === 'extraction') {
    return gt('Extraction', { context: 'schema type option' });
  }
  if (type === 'extension') {
    return gt('Extension', { context: 'schema type option' });
  }
  return gt('SQL', { context: 'schema type option' });
}

type SortableKeyChipProps = {
  id: string;
  label: string;
  onRemove: () => void;
  disabled: boolean;
};

function SortableKeyChip({
  id,
  label,
  onRemove,
  disabled,
}: SortableKeyChipProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-1 rounded border bg-muted/60 px-1.5 py-0.5 text-[11px] font-mono"
    >
      <span
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground/60 active:cursor-grabbing"
        aria-hidden
      >
        <DotsSixVerticalIcon className="h-3 w-3" />
      </span>
      <span>{label}</span>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="ml-0.5 text-muted-foreground hover:text-foreground disabled:opacity-40"
        aria-label={`Remove ${label} from key`}
      >
        <XIcon className="h-2.5 w-2.5" />
      </button>
    </div>
  );
}

export function QuickSchemaFieldsDialog({
  open,
  onOpenChange,
  title,
  presentation = 'dialog',
  initialFields,
  defaults,
  allowedSchemaTypes = DEFAULT_ALLOWED_SCHEMA_TYPES,
  onComplete,
}: QuickSchemaFieldsDialogProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const formId = useId();

  const [schemaDisplayName, setSchemaDisplayName] = useState(
    () => defaults?.schemaName ?? '',
  );
  const [schemaType, setSchemaType] = useState<QuickSchemaType>(
    () => defaults?.schemaType ?? 'extraction',
  );
  // An entity extension is a `sql` schema under the hood: same column types,
  // same flat field rows.
  const sqlColumns = schemaType !== 'extraction';
  const fieldTypeOptions: readonly string[] = sqlColumns
    ? SQL_QUICK_ADD_FIELD_TYPES
    : FRIENDLY_FIELD_TYPES;
  const [extensionTarget, setExtensionTarget] =
    useState<ExtensionTargetModel | null>(null);
  const targetModelLabel = useTargetModelLabel();
  const issueToasts = useQuickSchemaIssueToasts();
  const [rows, setRows] = useState<FriendlyFieldRow[]>(() =>
    initialFields ? importNodesToRows(initialFields) : [],
  );
  // Key membership is tracked by row id (not field key) so a field can be
  // toggled into the key before it has a name; nameless rows are dropped on save.
  const [keyRowIds, setKeyRowIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [jsonImportOpen, setJsonImportOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiNote, setAiNote] = useState<string | null>(null);

  const previewAi = usePreviewAssistImportFields();

  const sensors = useSensors(useSensor(PointerSensor));

  // Reset the form to a fresh state each time the dialog opens. Done during
  // render (guarded by the previous `open` value) rather than in an effect —
  // see "You Might Not Need an Effect" (resetting state on a prop change).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setSchemaDisplayName(defaults?.schemaName?.trim() ?? '');
      setSchemaType(defaults?.schemaType ?? 'extraction');
      setExtensionTarget(null);
      // Re-seed rather than clear: a caller that stays mounted and toggles
      // `open` would otherwise get an empty grid despite passing initialFields.
      setRows(initialFields ? importNodesToRows(initialFields) : []);
      setKeyRowIds([]);
      setSaving(false);
      setAiOpen(false);
      setAiInstruction('');
      setAiNote(null);
    }
  }

  const handleAddRow = useCallback(() => {
    setRows((r) => [...r, newRow()]);
  }, []);

  const handleRemoveRow = useCallback((id: string) => {
    setKeyRowIds((ids) => ids.filter((pkId) => pkId !== id));
    setRows((prev) => removeRowFromTree(prev, id));
  }, []);

  const handleUpdateRow = useCallback(
    (rowId: string, updater: (row: FriendlyFieldRow) => FriendlyFieldRow) => {
      setRows((prev) => updateRowInTree(prev, rowId, updater));
    },
    [],
  );

  const handleAddChild = useCallback((parentId: string) => {
    setRows((prev) => {
      const parent = findRowInTree(prev, parentId);
      if (!parent) return prev;
      return addChildToRow(prev, parentId, defaultChildRowForParent(parent));
    });
  }, []);

  const handleFieldNameChange = useCallback(
    (rowId: string, _oldName: string, newName: string) => {
      // Key membership is tracked by row id, so renaming needs no remapping.
      setRows((prev) =>
        updateRowInTree(prev, rowId, (row) => ({ ...row, fieldName: newName })),
      );
    },
    [],
  );

  const handleKeyCheck = useCallback((rowId: string, checked: boolean) => {
    setKeyRowIds((ids) =>
      checked
        ? ids.includes(rowId)
          ? ids
          : [...ids, rowId]
        : ids.filter((id) => id !== rowId),
    );
    if (checked) {
      // Key fields are implicitly required.
      setRows((prev) =>
        updateRowInTree(prev, rowId, (row) => ({ ...row, isRequired: true })),
      );
    }
  }, []);

  function handleKeyDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setKeyRowIds((ids) => {
        const oldIndex = ids.indexOf(active.id as string);
        const newIndex = ids.indexOf(over.id as string);
        return arrayMove(ids, oldIndex, newIndex);
      });
    }
  }

  const handleApplyAi = useCallback(() => {
    const trimmed = aiInstruction.trim();
    if (!trimmed || previewAi.isPending) return;

    const existingRootFieldNames = rows.flatMap(
      (r) => normalizeRootFieldKey(r.fieldName) || [],
    );

    previewAi.mutate(
      { instruction: trimmed, existingRootFieldNames },
      {
        onSuccess: (res) => {
          const merged = importNodesToRows(res.fields);
          setRows((prev) => {
            const keys = new Set(
              prev.flatMap((p) => normalizeRootFieldKey(p.fieldName) || []),
            );
            const appended = merged.filter(
              (m) => !keys.has(normalizeRootFieldKey(m.fieldName)),
            );
            return [...prev, ...appended];
          });
          setAiNote(res.explanation);
          setAiInstruction('');
        },
        onError: (err) => {
          showToast({
            title: gt('Could not generate fields', {
              context: 'toast when AI schema preview fails',
            }),
            message: err.message,
            color: 'error',
          });
        },
      },
    );
  }, [aiInstruction, previewAi, rows, showToast, gt]);

  function handleJsonImport(nodes: ImportFieldNode[], keyedBy: string[]) {
    const newRows = importNodesToRows(nodes);
    setRows((prev) => {
      const existingKeys = new Set(
        prev.flatMap((r) => normalizeRootFieldKey(r.fieldName) || []),
      );
      return [
        ...prev,
        ...newRows.filter(
          (r) => !existingKeys.has(normalizeRootFieldKey(r.fieldName)),
        ),
      ];
    });
    if (keyedBy.length > 0) {
      // `keyedBy` names the imported "sql" schema's composite key, in column
      // order — `newRows[].fieldName` are the exact same strings (both come
      // from `sqlSchemaParser`'s camelCase field names), so match directly.
      // Seeds `keyRowIds` so the Key chips reflect it immediately, without
      // the user re-checking each box.
      const newRowIdByFieldName = new Map(
        newRows.map((row) => [row.fieldName, row.id]),
      );
      const newKeyRowIds = keyedBy
        .map((fieldName) => newRowIdByFieldName.get(fieldName))
        .filter((id): id is string => id !== undefined);
      setKeyRowIds((prev) => [
        ...prev,
        ...newKeyRowIds.filter((id) => !prev.includes(id)),
      ]);
    }
    setJsonImportOpen(false);
  }

  async function handleSave() {
    const planned = planQuickSchemaSave({
      quickType: schemaType,
      displayName: schemaDisplayName,
      extensionTarget,
      // An entity has one extension, so it is named after the entity.
      extensionName: gt('{entity} extension', {
        context: 'generated name of an entity extension schema',
        entity: targetModelLabel(extensionTarget ?? ''),
      }),
      defaultTargetModel: defaults?.targetModel,
      rows,
      keyRowIds,
    });
    if (!planned.ok) {
      showToast(issueToasts[planned.issue]);
      return;
    }

    setSaving(true);
    try {
      const saved = await saveQuickSchema(planned.plan, quickSchemaSaveApi);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_BASE, 'get', saved.schemaDefinitionId],
        }),
        queryClient.invalidateQueries({ queryKey: ['schema-extensions'] }),
      ]);
      await onComplete(saved);
      setSaving(false);
      onOpenChange(false);
    } catch (err) {
      showToast({
        title: gt('Could not save schema', {
          context: 'toast when quick schema save fails',
        }),
        message: err instanceof Error ? err.message : String(err),
        color: 'error',
      });
      setSaving(false);
    }
  }

  const titleNode = title ?? (
    <T context="default title for quick schema fields dialog">New schema</T>
  );

  const body = (
    <>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6">
        {schemaType !== 'extension' && (
          <div className="space-y-2">
            <Label htmlFor={`${formId}-name`} className="text-sm">
              <T context="label schema display name quick dialog">Name</T>
            </Label>
            <Input
              id={`${formId}-name`}
              value={schemaDisplayName}
              onChange={(e) => setSchemaDisplayName(e.target.value)}
              placeholder={gt('e.g. Vendor webhook', {
                context: 'placeholder schema name quick dialog',
              })}
              disabled={saving}
            />
          </div>
        )}

        <div
          className={cn(
            'grid gap-3',
            schemaType === 'extension' ? 'grid-cols-2' : 'grid-cols-1',
          )}
        >
          <div className="space-y-2">
            <Label htmlFor={`${formId}-type`} className="text-sm">
              <T context="label schema type quick dialog">Schema type</T>
            </Label>
            <Select
              value={schemaType}
              onValueChange={(v) => {
                setSchemaType(v as QuickSchemaType);
                // An extension is keyed by its entity: drop any staged key.
                if (v === 'extension') setKeyRowIds([]);
              }}
              disabled={saving}
            >
              <SelectTrigger id={`${formId}-type`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allowedSchemaTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {schemaTypeSelectLabel(gt, type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {schemaType === 'extension' && (
            <ExtensionEntitySelect
              id={`${formId}-entity`}
              value={extensionTarget}
              onChange={setExtensionTarget}
              disabled={saving}
            />
          )}
        </div>

        {schemaType !== 'extension' && (
          <div className="space-y-2">
            <Label className="text-sm">
              <T context="label key quick schema dialog">Key</T>
            </Label>
            <div className="min-h-[2rem] rounded-md border bg-muted/20 px-2 py-1.5">
              {keyRowIds.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  <T context="empty key hint auto uuid quick schema">
                    auto-generated UUID
                  </T>
                </p>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleKeyDragEnd}
                >
                  <SortableContext
                    items={keyRowIds}
                    strategy={horizontalListSortingStrategy}
                  >
                    <div className="flex flex-wrap gap-1.5">
                      {keyRowIds.map((rowId) => {
                        const row = findRowInTree(rows, rowId);
                        const key = normalizeRootFieldKey(row?.fieldName ?? '');
                        const label =
                          key ||
                          gt('(unnamed)', {
                            context: 'placeholder label for unnamed key field',
                          });
                        return (
                          <SortableKeyChip
                            key={rowId}
                            id={rowId}
                            label={label}
                            onRemove={() =>
                              setKeyRowIds((ids) =>
                                ids.filter((id) => id !== rowId),
                              )
                            }
                            disabled={saving}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">
              <T context="key hint check field as key quick schema">
                Check the Key box on a field below to add it. Drag chips to
                reorder.
              </T>
            </p>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-sm">
              <T context="section label fields in quick schema dialog">
                Fields
              </T>
            </Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setJsonImportOpen(true)}
                disabled={saving}
              >
                <UploadSimpleIcon className="mr-1 h-3.5 w-3.5" aria-hidden />
                <T context="import from json button quick schema">
                  Import from JSON
                </T>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                onClick={handleAddRow}
                disabled={saving}
              >
                <PlusIcon className="mr-1 h-3.5 w-3.5" aria-hidden />
                <T context="add row quick schema">Add field</T>
              </Button>
            </div>
          </div>

          {rows.length === 0 ? (
            <p className="rounded-md border border-dashed bg-muted/30 px-3 py-6 text-center text-xs text-muted-foreground">
              <T context="empty state quick schema field list">
                No fields yet. Add a field or use optional AI suggestions below.
              </T>
            </p>
          ) : (
            <div className="space-y-3">
              {rows.map((row) => {
                const isKey = keyRowIds.includes(row.id);
                if (sqlColumns) {
                  // An extension is keyed by its entity, so it offers no Key.
                  const extension = schemaType === 'extension';
                  return (
                    <SqlSchemaFieldRow
                      key={row.id}
                      row={row}
                      formId={formId}
                      saving={saving}
                      isKey={!extension && isKey}
                      onFieldNameChange={handleFieldNameChange}
                      onUpdate={handleUpdateRow}
                      onRemove={handleRemoveRow}
                      onKeyCheck={extension ? undefined : handleKeyCheck}
                    />
                  );
                }
                // A row imported as "numeric" (via JSON) isn't in
                // `fieldTypeOptions` (no precision/scale inputs exist
                // here to newly create one — see SQL_QUICK_ADD_FIELD_TYPES),
                // but the Select still needs it listed so it shows the
                // row's actual type instead of rendering blank.
                const rowFieldTypeOptions = fieldTypeOptions.includes(
                  row.fieldType,
                )
                  ? fieldTypeOptions
                  : [...fieldTypeOptions, row.fieldType];
                return (
                  <QuickSchemaFieldRow
                    key={row.id}
                    row={row}
                    depth={0}
                    formId={formId}
                    saving={saving}
                    isRoot
                    isKey={isKey}
                    onFieldNameChange={handleFieldNameChange}
                    onUpdate={handleUpdateRow}
                    onRemove={handleRemoveRow}
                    onAddChild={handleAddChild}
                    onKeyCheck={handleKeyCheck}
                    schemaType={schemaType}
                    fieldTypeOptions={rowFieldTypeOptions}
                  />
                );
              })}
            </div>
          )}
        </div>

        {!sqlColumns && (
          <Collapsible open={aiOpen} onOpenChange={setAiOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className={cn(
                  'flex w-full items-center gap-1 rounded-md border border-transparent px-1 py-1.5 text-left text-xs font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                )}
                disabled={saving}
              >
                {aiOpen ? (
                  <CaretDownIcon className="h-3.5 w-3.5 shrink-0" />
                ) : (
                  <CaretRightIcon className="h-3.5 w-3.5 shrink-0" />
                )}
                <T context="collapsible trigger optional AI quick schema">
                  Optional: suggest fields with AI
                </T>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 pt-1">
              {aiNote ? (
                <p className="rounded-md border border-primary/15 bg-primary/5 px-2 py-1.5 text-[11px] text-muted-foreground">
                  {aiNote}
                </p>
              ) : null}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <Textarea
                  value={aiInstruction}
                  onChange={(e) => setAiInstruction(e.target.value)}
                  placeholder={gt(
                    'Describe the payload shape in plain language…',
                    {
                      context: 'placeholder AI assist quick schema',
                    },
                  )}
                  rows={2}
                  className="min-h-0 flex-1 resize-none text-xs"
                  disabled={saving || previewAi.isPending}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      handleApplyAi();
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="shrink-0"
                  disabled={
                    saving || previewAi.isPending || !aiInstruction.trim()
                  }
                  onClick={handleApplyAi}
                >
                  {previewAi.isPending ? (
                    <T context="AI apply loading quick schema">Working…</T>
                  ) : (
                    <T context="AI apply button quick schema">Add suggested</T>
                  )}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">
                <T context="AI shortcut hint quick schema">
                  Cmd+Enter to add suggested fields (merged with your list).
                </T>
              </p>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </>
  );

  const footerActions = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => onOpenChange(false)}
        disabled={saving}
      >
        <T context="cancel quick schema dialog">Cancel</T>
      </Button>
      <Button type="button" onClick={() => void handleSave()} disabled={saving}>
        {saving ? (
          <T context="save in progress quick schema">Saving…</T>
        ) : (
          <T context="save quick schema dialog">Save</T>
        )}
      </Button>
    </>
  );

  return (
    <>
      {presentation === 'sheet' ? (
        <Sheet open={open} onOpenChange={onOpenChange}>
          {/* Raised above the dialog layer so the sheet works when opened from
              inside a dialog. */}
          <SheetContent
            side="right"
            className="z-[1500] flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
            overlayClassName="z-[1500]"
          >
            <SheetHeader className="shrink-0 space-y-1 border-b px-4 py-3 text-left sm:px-6">
              <SheetTitle className="text-left">{titleNode}</SheetTitle>
              {SUBTITLE_NODE}
            </SheetHeader>
            {body}
            <SheetFooter className="shrink-0 flex-row justify-end gap-2 border-t bg-muted/30 px-4 py-3 sm:px-6">
              {footerActions}
            </SheetFooter>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="flex max-h-[min(90vh,720px)] max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
            <DialogHeader className="shrink-0 space-y-1 border-b px-4 py-3 text-left sm:px-6">
              <DialogTitle className="text-left">{titleNode}</DialogTitle>
              {SUBTITLE_NODE}
            </DialogHeader>
            {body}
            <DialogFooter className="shrink-0 gap-2 border-t bg-muted/30 px-4 py-3 sm:px-6">
              {footerActions}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <JsonFieldImportDialog
        open={jsonImportOpen}
        onOpenChange={setJsonImportOpen}
        onImport={handleJsonImport}
        schemaType={sqlColumns ? 'sql' : schemaType}
      />
    </>
  );
}
